import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { activityAvailabilityActions, checkAvailabilityHold, getActivity, prefetchCrossSell, activityActions, setSelectedActivityReviewClientCash, setSelectedActivityReviewClientCashStr } from '@share/store/slices';
import { Card, Typography, Button, Row, Col, Skeleton, Radio, Tag } from 'antd';
import { ClockCircleOutlined, GlobalOutlined, SafetyOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { openActivityCheckoutPage } from '@utils';
import { Toaster, getAccountUsernameFromPath, RootState, AppDispatch } from '@share/utils';
import moment from 'moment';
import { OfferExpiredModal } from '@components';

import './style.scss';


const { Title, Text } = Typography;

export interface CheckAvailabilityProps {
    activityId: string | undefined;
}

const HOLD_ERROR_EXPIRED = '1-372';
const HOLD_ERROR_OPERATION_EXPIRED = 'HOLD_OPERATION_ERROR';
const HOLD_ERROR_LIMIT_EXPIRED = 'HOLD_LIMIT_REACHED';
const EXPIRARATION_ERRORS = [HOLD_ERROR_EXPIRED, HOLD_ERROR_OPERATION_EXPIRED, HOLD_ERROR_LIMIT_EXPIRED];

export const CheckAvailability: React.FC<CheckAvailabilityProps> = ({ activityId }) => {

    const intl = useIntl();

    const location = useLocation();
    const history = useHistory();

    const dispatch = useDispatch<AppDispatch>();

    const accountName = getAccountUsernameFromPath(location);

    const { data, loading, selectedModalityCode, selectedSessionCode, selectedRateKey, selectedDate } = useSelector((state: RootState) => state.activityAvailabilityStore);
    const { value } = useSelector((state: RootState) => state.marginatorStore);
    const { account } = useSelector((state: RootState) => state.loginStore);
    const datesStore = useSelector((state: RootState) => state.datesStore);
    const { selectedActivitySearchClientCash } = useSelector((state: RootState) => state.activityStore);

    const [expandedDescription, setExpandedDescription] = React.useState<Record<string, boolean>>({});
    const [showAllTimes, setShowAllTimes] = React.useState<Record<string, boolean>>({});
    const [showExpiredModal, setShowExpiredModal] = React.useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    const getLanguageName = (code: string) => {
        const mapping: Record<string, string> = {
            en: "languages.english",
            es: "languages.spanish",
            fr: "languages.french",
            it: "languages.italian",
            pt: "languages.portuguese",
        };
        const id = mapping[code.toLowerCase()];
        if (id) {
            return intl.formatMessage({ id });
        }
        return code.charAt(0).toUpperCase() + code.slice(1);
    };

    // "Wed 27 Aug" — append T00:00:00 to avoid UTC parsing shifting the day.
    const formatDayLabel = (date?: string) => {
        if (!date) return '';
        const datePart = date.slice(0, 10); // accept both "2026-08-27" and "2026-08-27T00:00:00"
        const d = new Date(`${datePart}T00:00:00`);
        if (isNaN(d.getTime())) return datePart;
        return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    };

    const handleSessionSelect = (modalityCode: string, rateKey: string, sessionCode: string, date?: string) => {
        dispatch(activityAvailabilityActions.setSelectedModality(modalityCode));
        dispatch(activityAvailabilityActions.setSelectedRate(rateKey));
        dispatch(activityAvailabilityActions.setSelectedSession(sessionCode));
        dispatch(activityAvailabilityActions.setSelectedDate(date));
    };

    useEffect(() => {
        if ((data?.modalities?.length ?? 0) > 0 && !selectedSessionCode && !selectedRateKey && !loading) {
            let sessionToSelect = null;
            for (const modality of (data?.modalities ?? [] as any[])) {
                const availableSession = (modality.rates?.flatMap((rate: any) =>
                    rate.sessions?.map((session: any) => ({
                        ...session,
                        rateKey: rate.rateKey,
                        modalityCode: modality.code
                    })) || []
                ) || []).sort((a: any, b: any) => (a.date || "").localeCompare(b.date || "") || (a.name || "").localeCompare(b.name || ""))
                    .find((s: any) => s.available);

                if (availableSession) {
                    sessionToSelect = availableSession;
                    break;
                }
            }

            if (sessionToSelect) {
                handleSessionSelect(sessionToSelect.modalityCode, sessionToSelect.rateKey, sessionToSelect.code, sessionToSelect.date);
            } else {
                const firstModality = data?.modalities[0];
                const firstRate = firstModality?.rates?.[0];
                if (firstRate) {
                    const firstSession = firstRate.sessions?.[0];
                    handleSessionSelect(firstModality?.code, firstRate.rateKey, firstSession?.code || "", firstSession?.date);
                }
            }
        }
    }, [data, loading, selectedSessionCode, selectedRateKey]);

    useEffect(() => {
        if (!loading && (data?.modalities?.length ?? 0) > 0) {
            const timer = setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [loading, data]);

    if (loading) {
        return (
            <div className="check-availability-skeleton">
                <Card className="modality-card skeleton">
                    <Skeleton active avatar={{ size: 'small' }} paragraph={{ rows: 3 }} />
                </Card>
            </div>
        );
    }

    if (!data || !data.modalities || data.modalities.length === 0) {
        return null;
    }

    const bookActivity = () => {
        if (!activityId || !selectedRateKey) {
            return;
        }

        // Warm the cross-sell recommendations as soon as the user commits to booking,
        // so they run in parallel with the availability hold + checkout and are cached
        // by the time the confirmation screen renders them. The backend decides which
        // verticals are active, so there's no front-end gating.
        const crossSellCheckIn = datesStore?.startDate ? moment(datesStore.startDate).format('YYYY-MM-DD') : undefined;
        if (crossSellCheckIn) {
            dispatch(prefetchCrossSell({ origin: 'activity', propertyId: activityId, checkIn: crossSellCheckIn }));
        }

        // Clicking "Book Now" always copies the Real Cash picked on SEARCH into the review value,
        // overwriting whatever was selected on a previous checkout visit (mirrors hotels). This
        // keeps checkout consistent with search — including 0 — instead of retaining a stale value.
        // The value is also mirrored onto the shared client-cash object so it persists to the URL
        // and re-hydrates on the checkout page.
        const searchClientCash = selectedActivitySearchClientCash?.selectedSearchClientCash ?? 0;
        dispatch(setSelectedActivityReviewClientCash(searchClientCash));
        dispatch(setSelectedActivityReviewClientCashStr(searchClientCash ? searchClientCash.toString() : ''));
        dispatch(activityActions.setSelectedActivitySearchClientCash({
            ...selectedActivitySearchClientCash,
            selectedPropertyReviewClientCash: searchClientCash,
        }));

        dispatch(checkAvailabilityHold({
            activityCode: activityId,
            startTime: selectedSessionCode,
            rateKey: selectedRateKey,
            serviceDate: selectedDate, // chosen operation day; falls back to search From when undefined
            onSuccess: () => {
                openActivityCheckoutPage(activityId!, selectedRateKey!, history);
            },
            onFailure: (error, errorMessage) => {
                if (EXPIRARATION_ERRORS.includes(error)) {
                    setShowExpiredModal(true);
                } else {
                    Toaster.error(intl.formatMessage({ id: `error.code.activity.${error}`, defaultMessage: errorMessage }));
                }
            },
        }));
    };

    const handleBackToSearch = () => {
        dispatch(getActivity({ isLoadMore: false, isFiltersChange: false, isReuseSessionKey: false }));
    };

    return (
        <>
        <div className="check-availability-container" ref={containerRef}>
            {data.modalities.map((modality: any) => {
                const isSelected = selectedModalityCode === modality.code;

                const metric = modality.durationMetric;
                const formattedMetric = metric
                    ? metric.charAt(0).toUpperCase() + metric.slice(1).toLowerCase()
                    : "";
                const duration = modality.durationValue ? `${modality.durationValue} ${formattedMetric}` : "";

                const languagesList = Array.from(
                    new Set(
                        modality.languageGuides
                            ?.map((lg: any) => lg.language)
                            .filter(Boolean)
                    )
                )
                    .map((code) => getLanguageName(code as string))
                    .join(", ");

                // Aggregate all sessions from all rates (each session now carries its Date; the rate its GuideLanguage).
                const allSessions = (modality.rates?.flatMap((rate: any) =>
                    rate.sessions?.map((session: any) => ({
                        ...session,
                        rateKey: rate.rateKey,
                        guideLanguage: rate.guideLanguage,
                        price: rate.price,
                        saving: rate.saving,
                        pubPrice: rate.publicPrices,
                        currency: rate.currency,
                        freeCancellation: rate.freeCancellation,
                        cancellationPolicies: rate.cancellationPolicies,
                        refundable: rate.refundable
                    })) || []
                ) || []);

                // HotelBeds: days come from session.date (per-rate, with times and language).
                // Viator: sessions have no date; use activity.availableDates (pre-computed bookable days in the search window).
                const sessionDays: string[] = (() => {
                    const fromSessions = Array.from(
                        new Set(allSessions.map((s: any) => s.date).filter(Boolean))
                    ).sort() as string[];
                    if (fromSessions.length > 0) return fromSessions;
                    const fromAvailable = (data as any).availableDates as string[] | undefined;
                    return fromAvailable ? [...fromAvailable].sort() : [];
                })();
                const activeDay = (selectedDate && sessionDays.includes(selectedDate)) ? selectedDate : sessionDays[0];

                // Slots for the active day (or all when there is no per-date info).
                const daySessions = allSessions
                    .filter((s: any) => !s.date || s.date === activeDay)
                    .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));

                // When the same time appears for more than one language in the same day, label the language.
                const timeCounts: Record<string, number> = daySessions.reduce((acc: any, s: any) => {
                    acc[s.name] = (acc[s.name] || 0) + 1;
                    return acc;
                }, {});

                // Match by code+rateKey; for Viator date-agnostic sessions (date null), skip the date check.
                const selectedSession = daySessions.find((s: any) =>
                    s.code === selectedSessionCode && s.rateKey === selectedRateKey && (!s.date || s.date === selectedDate));
                const displayRate = selectedSession || daySessions.find((s: any) => s.available) || daySessions[0] || modality.rates?.find((r: any) => r.rateKey === selectedRateKey) || modality.rates?.[0] || {};

                // Marginator Logic
                const marginatorValue = value || 0;
                const price = displayRate.price || 0;
                const marginatorPrice = price / (1 - marginatorValue / 100);
                const marginatorSavings = (displayRate.saving || 0) / (1 - marginatorValue / 100);

                let pubPriceNum = marginatorPrice;
                if (Array.isArray(displayRate.pubPrice) && displayRate.pubPrice.length > 0) {
                    pubPriceNum = Math.min(...displayRate.pubPrice.map((p: any) => p.price));
                }

                const percents = 100;
                const msave = pubPriceNum
                    ? account?.breakdownTaxes
                        ? (1 - marginatorSavings / pubPriceNum) * percents
                        : (marginatorPrice * percents) / pubPriceNum
                    : 100;
                const marginatorsavePercents = Math.floor(percents - msave);

                const visibleSessions = showAllTimes[modality.code] ? daySessions : daySessions.slice(0, 10);
                const hasMoreTimes = daySessions.length > 10;

                const selectDay = (day: string) => {
                    // First try sessions tagged to this exact date (HotelBeds).
                    // Fall back to date-agnostic sessions (Viator: date is null/undefined for all days in window).
                    const exactSlots = allSessions.filter((s: any) => s.date === day);
                    const agnosticSlots = allSessions.filter((s: any) => !s.date);
                    const slots = exactSlots.length > 0 ? exactSlots : agnosticSlots;
                    const first = slots.find((s: any) => s.available) || slots[0];
                    if (first) {
                        handleSessionSelect(modality.code, first.rateKey, first.code, day);
                    } else {
                        dispatch(activityAvailabilityActions.setSelectedDate(day));
                    }
                };

                const toggleDescription = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setExpandedDescription(prev => ({ ...prev, [modality.code]: !prev[modality.code] }));
                };

                const toggleTimes = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setShowAllTimes(prev => ({ ...prev, [modality.code]: !prev[modality.code] }));
                };

                return (
                    <Card
                        key={modality.code}
                        className={`modality-card ${isSelected ? 'selected' : 'collapsed'}`}
                        onClick={() => {
                            if (!isSelected) {
                                // Select first available session of this modality
                                const firstAvailable = allSessions.find((s: any) => s.available) || allSessions[0];
                                const rateKey = firstAvailable?.rateKey || modality.rates?.[0]?.rateKey;
                                const sessionCode = firstAvailable?.code || "";
                                handleSessionSelect(modality.code, rateKey, sessionCode, firstAvailable?.date);
                            }
                        }}
                    >
                        <Row gutter={[0, 0]} align="stretch">
                            <Col xs={24} md={isSelected ? 16 : 18} className="modality-main">
                                <div className="modality-header">
                                    <div className="title-row">
                                        <Radio checked={isSelected} className="modality-radio">
                                            <Title level={4} className="modality-title">{modality.name}</Title>
                                        </Radio>
                                    </div>

                                    {isSelected && (
                                        <>
                                            <div className="modality-meta">
                                                {duration && (
                                                    <div className="meta-item">
                                                        <ClockCircleOutlined className="meta-icon" />
                                                        <Text type="secondary">{duration}</Text>
                                                    </div>
                                                )}
                                                {languagesList && (
                                                    <div className="meta-item">
                                                        <GlobalOutlined className="meta-icon" />
                                                        <Text type="secondary">{languagesList}</Text>
                                                    </div>
                                                )}
                                            </div>
                                            {modality.description && (
                                                <div className="description-container">
                                                    <Text type="secondary" className={`modality-description ${expandedDescription[modality.code] ? 'expanded' : 'clamped'}`}>
                                                        {modality.description}
                                                    </Text>
                                                    {modality.description.length > 150 && (
                                                        <Button type="link" onClick={toggleDescription} className="read-more-btn">
                                                            {expandedDescription[modality.code] ? 'Read less' : 'Read more'} {expandedDescription[modality.code] ? <UpOutlined /> : <DownOutlined />}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}

                                            {sessionDays.length > 1 && (
                                                <div className="day-selector">
                                                    <Text type="secondary" className="day-selector-label">
                                                        <FormattedMessage id="activity.details.selectDate" defaultMessage="Select a date" />
                                                    </Text>
                                                    <div className="day-selector-list">
                                                        {sessionDays.map((day: string) => (
                                                            <Button
                                                                key={day}
                                                                type={activeDay === day ? 'primary' : 'default'}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    selectDay(day);
                                                                }}
                                                                className={`day-button ${activeDay === day ? 'day-selected' : ''}`}
                                                            >
                                                                {formatDayLabel(day)}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {allSessions.some((s: any) => s.name?.trim()) && (
                                                <div className="sessions-container">
                                                    <div className="sessions-list">
                                                        {visibleSessions
                                                            .filter((session: any) => session.name?.trim())
                                                            .map((session: any) => {
                                                                const showLang = (timeCounts[session.name] || 0) > 1 && session.guideLanguage;
                                                                const label = showLang
                                                                    ? `${session.name} · ${getLanguageName(session.guideLanguage)}`
                                                                    : session.name;
                                                                // For date-agnostic sessions (Viator, date=null) use activeDay so
                                                                // the selected date is preserved when the user picks a time slot.
                                                                const effectiveDate = session.date ?? activeDay;
                                                                const isSlotSelected = selectedSessionCode === session.code
                                                                    && selectedRateKey === session.rateKey
                                                                    && effectiveDate === (selectedDate ?? activeDay);
                                                                return (
                                                                    <Button
                                                                        key={`${session.rateKey}-${session.code}-${session.date ?? ''}`}
                                                                        type={isSlotSelected ? 'primary' : 'default'}
                                                                        disabled={!session.available}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleSessionSelect(modality.code, session.rateKey, session.code, effectiveDate);
                                                                        }}
                                                                        className={`session-button ${isSlotSelected ? 'session-selected' : ''}`}
                                                                    >
                                                                        {label}
                                                                    </Button>
                                                                );
                                                            })}
                                                    </div>
                                                    {hasMoreTimes && (
                                                        <Button type="link" onClick={toggleTimes} className="see-more-times-btn">
                                                            {showAllTimes[modality.code] ? 'See Less Times' : 'See More Times'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}

                                            {(displayRate.cancellationPolicies?.length > 0 && displayRate?.refundable !== false) && (
                                                <div className="cancellation-policy-container">
                                                    <div className="policy-badge">
                                                        <SafetyOutlined className="policy-icon" />
                                                        <Text className="policy-text">
                                                            {displayRate.freeCancellation === true ? (
                                                                <FormattedMessage id="activity.details.cancellationFee.notApplicable" defaultMessage="Cancellation fee Not Applicable" />) : (
                                                                <FormattedMessage id="activity.details.cancellationFee.applicable" defaultMessage="Cancellation fee Applicable" />)}
                                                        </Text>
                                                    </div>
                                                </div>)}

                                            {(displayRate?.refundable === true || displayRate?.refundable === false) && (
                                                <div className="cancellation-policy-container__refundable">
                                                    <div className={`activity-card-wrapper__refundable-${displayRate.refundable ? 'ref' : 'nonref'}`}>
                                                        {displayRate.refundable ? (
                                                            <FormattedMessage id="refundable" />) : (
                                                            <FormattedMessage id="non.refundable" />)}
                                                    </div>
                                                </div>)}

                                        </>
                                    )}
                                </div>
                            </Col>

                            <Col xs={24} md={isSelected ? 8 : 6} className="modality-price-actions">
                                <div className="price-info">
                                    {marginatorsavePercents > 0 && (
                                        <div className="original-price" style={{ marginBottom: '4px' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                <Text delete>
                                                    US $ {pubPriceNum.toFixed(2)}
                                                </Text>
                                            </Text>
                                        </div>
                                    )}
                                    <div className="current-price" style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Title level={isSelected ? 3 : 4} className="price-amount" style={{ margin: 0 }}>
                                            US $ {marginatorPrice.toFixed(2)}
                                        </Title>
                                        {marginatorsavePercents > 0 && (
                                            <Tag color="red" className="savings-tag" style={{ margin: 0 }}>
                                                <FormattedMessage id="activity.details.save" defaultMessage="Save" /> {marginatorsavePercents}%
                                            </Tag>
                                        )}
                                    </div>
                                    {isSelected && marginatorsavePercents > 0 && (
                                        <div className="savings-info" style={{ marginBottom: '12px' }}>
                                            <Text type="danger" strong style={{ fontSize: '13px' }}>
                                                <FormattedMessage id="activity.details.saving" defaultMessage="SAVING" />: US $ {marginatorSavings.toFixed(2)}
                                            </Text>
                                        </div>
                                    )}

                                </div>
                                {isSelected && (
                                    <Button
                                        type="default"
                                        size="large"
                                        className="book-now-button"
                                        onClick={() => bookActivity()}
                                    >
                                        <FormattedMessage id="activity.details.bookNow" defaultMessage="Book Now" />
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Card>
                );
            })}
        </div>
        <OfferExpiredModal
            visible={showExpiredModal}
            backToSearch={true}
            accountName={accountName}
            activityCode={activityId}
            onBackToSearch={handleBackToSearch}
        />
        </>
    );
};
